import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { concertService, ConcertQueryParams } from '@/services/concertService';
import { CreateConcertRequest } from '@/types/model';

export const concertKeys = {
  all: ['concerts'] as const,
  lists: () => [...concertKeys.all, 'list'] as const,
  list: (params: ConcertQueryParams) => [...concertKeys.lists(), params] as const,
  details: () => [...concertKeys.all, 'detail'] as const,
  detail: (id: string) => [...concertKeys.details(), id] as const,
};

export const useConcerts = (params: ConcertQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: concertKeys.list(params),
    queryFn: () => concertService.getConcerts(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useConcert = (id: string) => {
  return useQuery({
    queryKey: concertKeys.detail(id),
    queryFn: () => concertService.getConcertById(id),
    enabled: !!id,
  });
};

export const useCreateConcert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (concertData: CreateConcertRequest) =>
      concertService.createConcert(concertData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() });
    },
  });
};

export const useUpdateConcert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateConcertRequest> }) =>
      concertService.updateConcert(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: concertKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() });
    },
  });
};

export const useDeleteConcert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => concertService.deleteConcert(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() });
    },
  });
};

export const useToggleConcertStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      concertService.toggleConcertStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: concertKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: concertKeys.lists() });
    },
  });
};